import {
    ForwardedRef,
    forwardRef,
    memo,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

import {
    TransformWrapper,
    TransformComponent,
    ReactZoomPanPinchContentRef,
} from "react-zoom-pan-pinch";

import { styled } from "@mui/material";
import { CommonProps } from "@mui/material/OverridableComponent";

import { useAppSelector } from "app/hooks";
import { playbackDebugSelectors } from "features/cases/caseViewer/store/selectors.debug";

import { Property } from "csstype";

import { selectInWindowMode } from "features/core/store/selectors";

import { PlayerRef } from "./types/PlayerRef";
import { PlayMode } from "./types/PlayMode";

import { VideoWithOverlay } from "./VideoWithOverlay";

export interface NativePlayerProps {
    url?: string;
    playMode: PlayMode;
    playSpeed: number;
    enableAudio: boolean;
    audioEnabledByDefault: boolean;
    onCanPlayThrough?: () => void;
    onWaiting?: () => void;
    onError?: () => void;
    onClickVideo?: () => void;
    onSeeked?: () => void;
    onStalled?: () => void;
    onSuspend?: () => void;
    onDurationChange?: () => void;
    videoMouseOverCursor?: Property.Cursor;
    zoomEnabled?: boolean;
    hidden?: boolean;
    enableOverlay?: boolean;
    showControls?: boolean;
}

const NativePlayerComponent = memo(
    forwardRef<PlayerRef, NativePlayerProps>(
        (
            props: CommonProps & NativePlayerProps,
            ref: ForwardedRef<PlayerRef>,
        ) => {
            // debug
            const debugShowSystemControls = useAppSelector(
                playbackDebugSelectors.selectShowSystemControls,
            );

            const videoElementRef = useRef<HTMLVideoElement>(null);
            const [isMuted, setIsMuted] = useState(false);

            useImperativeHandle(ref, () => {
                return {
                    reloadStream: () => {
                        // not needed?
                    },
                    reinitializePlayer: () => {
                        // not needed?
                    },
                    videoSetCurrentTime(currentTime: number) {
                        if (videoElementRef.current) {
                            videoElementRef.current.currentTime = currentTime;
                        }
                    },
                    isReadyToPlay: () => {
                        return (
                            (videoElementRef.current &&
                                videoElementRef.current.readyState > 1) ??
                            false
                        );
                    },
                };
            }, [props.url]);

            // Used to control video for playback
            useEffect(() => {
                if (videoElementRef.current && props.playMode !== "autoPlay") {
                    const videoElement =
                        videoElementRef.current as HTMLVideoElement;

                    switch (props.playMode) {
                        case "play":
                            videoElement.playbackRate = props.playSpeed;

                            videoElement.play();

                            break;

                        case "pause":
                            videoElement.pause();
                            break;

                        case "stop":
                            videoElement.pause();
                            break;
                    }
                }
            }, [videoElementRef, props.playMode, props.playSpeed]);

            // On windowmode we must reset the zoom level
            const inWindowMode = useAppSelector(selectInWindowMode);
            const transformWrapperRef =
                useRef<ReactZoomPanPinchContentRef>(null);

            useEffect(() => {
                if (inWindowMode && transformWrapperRef.current) {
                    transformWrapperRef.current.resetTransform();
                }
            }, [inWindowMode]);

            return (
                <>
                    <TransformWrapper
                        ref={transformWrapperRef}
                        disabled={inWindowMode}
                    >
                        {({ resetTransform }) => (
                            <TransformComponent
                                wrapperStyle={{
                                    width: "100%",
                                    height: "100%",
                                }}
                                contentStyle={{
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                <VideoWithOverlay
                                    showOverlay={
                                        !inWindowMode &&
                                        props.enableOverlay === true
                                    }
                                    audioEnabledByDefault={
                                        props.audioEnabledByDefault
                                    }
                                    enableAudio={props.enableAudio}
                                    isMuted={isMuted}
                                    onSetIsMuted={(x) => setIsMuted(x)}
                                >
                                    <video
                                        preload="metadata"
                                        ref={videoElementRef}
                                        playsInline
                                        controls={
                                            props.showControls ||
                                            debugShowSystemControls
                                        }
                                        autoPlay={props.playMode === "autoPlay"}
                                        muted={isMuted || !props.enableAudio}
                                        style={{
                                            backgroundColor: "#000",
                                            width: "100%",
                                            height: "100%",
                                            cursor:
                                                props.videoMouseOverCursor ||
                                                "initial",
                                        }}
                                        onDurationChange={
                                            props.onDurationChange
                                        }
                                        onStalled={props.onStalled}
                                        onSuspend={props.onSuspend}
                                        onCanPlayThrough={
                                            props.onCanPlayThrough
                                        }
                                        onWaiting={props.onWaiting}
                                        onError={props.onError}
                                        onClick={() => {
                                            resetTransform();
                                            if (props.onClickVideo) {
                                                props.onClickVideo();
                                            }
                                        }}
                                        onSeeked={props.onSeeked}
                                        src={props.url}
                                        crossOrigin={"use-credentials"}
                                    />
                                </VideoWithOverlay>
                            </TransformComponent>
                        )}
                    </TransformWrapper>
                </>
            );
        },
    ),
);

export const NativePlayer = styled(NativePlayerComponent)(() => ({}));
